import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { Bug, Apple, Play } from "lucide-react";

const HOLES = 9;
const TIME = 30;
const FACTS = [
  "Sugar bugs are really bacteria. They eat sugar and make acid that can make holes (cavities) in teeth.",
  "Brushing twice a day for 2 minutes sweeps sugar bugs away before they can do damage.",
  "Drinking water after snacks helps wash food and sugar off your teeth.",
];

export const SugarBugSmash = () => {
  const [time, setTime] = useState(0);
  const [score, setScore] = useState(0);
  const [cells, setCells] = useState(Array(HOLES).fill(null));
  const [played, setPlayed] = useState(false);
  const scoreRef = useRef(0);
  const running = time > 0;

  useEffect(() => {
    if (!running) return undefined;
    const t = setTimeout(() => setTime((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [time, running]);

  useEffect(() => {
    if (!running) { setCells(Array(HOLES).fill(null)); return undefined; }
    const spawn = setInterval(() => {
      setCells(() => {
        const next = Array(HOLES).fill(null);
        const n = 1 + Math.floor(Math.random() * 2);
        for (let k = 0; k < n; k++) next[Math.floor(Math.random() * HOLES)] = Math.random() < 0.75 ? "bug" : "apple";
        return next;
      });
    }, 900);
    return () => clearInterval(spawn);
  }, [running]);

  useEffect(() => {
    if (played && time === 0 && scoreRef.current >= 15) confetti({ particleCount: 120, spread: 90 });
  }, [time, played]);

  const hit = (i) => {
    const c = cells[i];
    if (!c || !running) return;
    const d = c === "bug" ? 1 : -2;
    scoreRef.current = Math.max(0, scoreRef.current + d);
    setScore(scoreRef.current);
    setCells((cs) => cs.map((x, k) => (k === i ? null : x)));
  };

  const start = () => { scoreRef.current = 0; setScore(0); setPlayed(true); setTime(TIME); };

  return (
    <div className="rounded-[2.5rem] bg-[#fff1f1] p-6 sm:p-10" data-testid="game-sugar-bug">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-bold sm:text-3xl">Sugar Bug Smash</h2>
        <div className="flex gap-2 text-sm font-bold">
          <span className="rounded-full bg-white px-3 py-1.5" data-testid="sugar-bug-score">Score: {score}</span>
          <span className="rounded-full bg-white px-3 py-1.5" data-testid="sugar-bug-time">Time: {time}s</span>
        </div>
      </div>
      <p className="mt-2 text-sm font-semibold text-slate-600">Tap the sugar bugs to brush them off! Don't tap the apples - they're tooth friends (-2 points).</p>
      <div className="mx-auto mt-6 grid max-w-md grid-cols-3 gap-3">
        {cells.map((c, i) => (
          <button key={i} onClick={() => hit(i)} data-testid={`sugar-bug-cell-${i}`} aria-label={c || "empty tooth"}
            className="relative flex aspect-square items-center justify-center rounded-[1.5rem] border-[3px] border-slate-800 bg-white transition-transform active:scale-95">
            {c === "bug" && <Bug className="h-12 w-12 animate-bounce text-lime-600" />}
            {c === "apple" && <Apple className="h-12 w-12 text-[#ff6b6b]" />}
          </button>
        ))}
      </div>
      {!running && (
        <div className="mt-6 text-center">
          {played && <p className="font-display text-xl font-semibold text-slate-800" data-testid="sugar-bug-result">You smashed {score} sugar bugs! {score >= 15 ? "Super brusher!" : "Try again to beat 15!"}</p>}
          {played && <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">Did you know? {FACTS[score % FACTS.length]}</p>}
          <button onClick={start} data-testid="sugar-bug-start"
            className="btn-pop shadow-chunky-coral mt-4 inline-flex items-center gap-2 rounded-full bg-[#ff6b6b] px-7 py-3.5 font-bold text-white"><Play className="h-5 w-5" />{played ? "Play again" : "Start game"}</button>
        </div>
      )}
    </div>
  );
};
